import TemplatesApi from './Templates.api';
import { isEditableVideoTemplate } from './Template.model';

/**
 * Debounce utility with cancel support.
 * @param {Function} func - The function to debounce.
 * @param {number} delay - The delay in milliseconds.
 * @returns {Function} The debounced function with a cancel method.
 */
export const debounce = (func, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };

  debounced.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debounced;
};

/**
 * Fetches topics and templates from the API.
 * @returns {Promise<{topics: Array, templates: Array}>} The fetched data.
 */
export const fetchFiltersAndTemplates = async () => {
  const [topicsResponse, templatesResponse] = await Promise.all([
    TemplatesApi.getTopics(),
    TemplatesApi.getTemplates(),
  ]);
  return { topics: topicsResponse, templates: templatesResponse };
};

/**
 * Deletes a topic by ID.
 * @param {string} topicId - The ID of the topic to delete.
 * @throws {Error} If the deletion fails.
 */
export const deleteTopic = async (topicId) => {
  await TemplatesApi.deleteTopic(topicId);
};

/**
 * Deletes a subtopic by ID.
 * @param {string} subtopicId - The ID of the subtopic to delete.
 * @throws {Error} If the deletion fails.
 */
export const deleteSubtopic = async (subtopicId) => {
  await TemplatesApi.deleteSubtopic(subtopicId);
};

export const AVAILABILITY = {
  LIVE: 'live',
  UPCOMING: 'upcoming',
  EXPIRED: 'expired',
};

export const TEMPLATE_TYPE = {
  SCHEDULED: 'scheduled',
  EVERGREEN: 'evergreen',
};

/**
 * Returns the availability status of a template based on its start/end dates.
 * @param {Object} template
 * @returns {'live'|'upcoming'|'expired'}
 */
export const getTemplateAvailability = (template) => {
  const now = new Date();
  const startDate = template.start_date ? new Date(template.start_date) : null;
  const endDate = template.end_date ? new Date(template.end_date) : null;
  if (endDate && endDate <= now) return AVAILABILITY.EXPIRED;
  if (startDate && startDate > now) return AVAILABILITY.UPCOMING;
  return AVAILABILITY.LIVE;
};

/**
 * Returns whether a template is still active, meaning it has not ended yet.
 * Templates scheduled to start later count as active, which is the same
 * notion of active used when ending a topic's or subtopic's templates.
 * @param {Object} template
 * @returns {boolean} True when the template has not ended.
 */
export const isTemplateActive = (template) =>
  getTemplateAvailability(template) !== AVAILABILITY.EXPIRED;

/**
 * Returns whether a template is evergreen (no end date) or scheduled.
 * @param {Object} template
 * @returns {'scheduled'|'evergreen'}
 */
export const getTemplateType = (template) =>
  template.end_date ? TEMPLATE_TYPE.SCHEDULED : TEMPLATE_TYPE.EVERGREEN;

const STATUS_MATCHERS = {
  live: (template) => getTemplateAvailability(template) === AVAILABILITY.LIVE,
  upcoming: (template) =>
    getTemplateAvailability(template) === AVAILABILITY.UPCOMING,
  evergreen: (template) =>
    getTemplateType(template) === TEMPLATE_TYPE.EVERGREEN,
  ended: (template) =>
    getTemplateAvailability(template) === AVAILABILITY.EXPIRED,
};

function listFilterValues(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function templateMatchesType(template, type) {
  if (type === 'video') {
    return isEditableVideoTemplate(template);
  }

  return template.category === type;
}

function parseFilterDay(value, endOfDay) {
  if (!value) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  }
  return date;
}

function templateOverlapsDateRange(template, dateFrom, dateTo) {
  const rangeStart = parseFilterDay(dateFrom, false);
  const rangeEnd = parseFilterDay(dateTo, true);
  const start = template.start_date ? new Date(template.start_date) : null;
  const end = template.end_date ? new Date(template.end_date) : null;
  const templateStart =
    start && !Number.isNaN(start.getTime()) ? start : new Date(0);
  const templateEnd =
    end && !Number.isNaN(end.getTime()) ? end : new Date(8640000000000000);

  if (rangeStart && templateEnd < rangeStart) return false;
  if (rangeEnd && templateStart > rangeEnd) return false;
  return true;
}

function relationId(relation) {
  if (relation == null || relation === '') return '';
  if (typeof relation === 'object') return relation.id ?? '';
  return relation;
}

function sameId(left, right) {
  const normalizedLeft = String(relationId(left));
  const normalizedRight = String(relationId(right));
  return normalizedLeft !== '' && normalizedLeft === normalizedRight;
}

function templateMatchesTopicOrSubtopic(template, id, type) {
  const relation = type === 'topic' ? template.topic : template.subtopic;
  return sameId(relation, id);
}

/**
 * Filters templates based on provided filters.
 * @param {Array} templates - The full list of templates.
 * @param {Object} filters - The filter criteria.
 * @param {boolean} showInactive - When false, inactive (expired) templates are
 * hidden, unless an explicit availability filter is applied. The list shows
 * them by default so ended templates stay visible without the Ended filter.
 * @returns {Array} The filtered templates.
 */
export const filterTemplates = (templates, filters, showInactive = true) => {
  let filtered = templates;
  const types = listFilterValues(filters.types);
  if (types.length) {
    filtered = filtered.filter((template) =>
      types.some((type) => templateMatchesType(template, type))
    );
  } else if (filters.postType && filters.postType !== 'all') {
    filtered = filtered.filter(
      (template) => template.category === filters.postType
    );
  }
  if (filters.topic && filters.topic !== 'all') {
    filtered = filtered.filter((template) =>
      sameId(template.topic, filters.topic)
    );
  }
  if (filters.subtopic && filters.subtopic !== 'all') {
    filtered = filtered.filter((template) =>
      sameId(template.subtopic, filters.subtopic)
    );
  }
  const statuses = listFilterValues(filters.statuses);
  if (statuses.length) {
    filtered = filtered.filter((template) =>
      statuses.some((status) => STATUS_MATCHERS[status]?.(template))
    );
  } else if (filters.availability && filters.availability !== 'all') {
    filtered = filtered.filter(
      (template) => getTemplateAvailability(template) === filters.availability
    );
  } else if (!showInactive) {
    filtered = filtered.filter(isTemplateActive);
  }

  if (filters.dateFrom || filters.dateTo) {
    filtered = filtered.filter((template) =>
      templateOverlapsDateRange(template, filters.dateFrom, filters.dateTo)
    );
  }
  if (filters.type && filters.type !== 'all') {
    filtered = filtered.filter(
      (template) => getTemplateType(template) === filters.type
    );
  }
  return filtered;
};

/**
 * Sorts templates so that active (live and upcoming) ones come first, then
 * expired ones. Within each group, templates are ordered by create date
 * descending.
 * @param {Array} templates
 * @returns {Array} A new sorted array.
 */
export const sortTemplates = (templates) => {
  return [...templates].sort((a, b) => {
    const aActive = isTemplateActive(a);
    const bActive = isTemplateActive(b);
    if (aActive !== bActive) return aActive ? -1 : 1;
    const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bCreated - aCreated;
  });
};

/**
 * Searches templates by message content or template id (substring match).
 * @param {Array} templates - The list of templates to search.
 * @param {string} searchQuery - The search term.
 * @returns {Array} The filtered templates.
 */
export const searchTemplates = (templates, searchQuery = '') => {
  const normalizedQuery = normalizeString(searchQuery.trim());
  return templates.filter((template) => {
    const messageMatch = normalizeString(template.message).includes(
      normalizedQuery
    );
    const idMatch = normalizeString(template.id).includes(normalizedQuery);
    return messageMatch || idMatch;
  });
};

/**
 * Sorts templates by created_time descending (newest first).
 * Templates without created_time are listed last.
 * @param {Array} templates - The list of templates to sort.
 * @returns {Array} Templates sorted newest first.
 */
export const sortTemplatesByCreatedTime = (templates) => {
  return [...templates].sort((a, b) => {
    const aTime = Date.parse(a.created_time);
    const bTime = Date.parse(b.created_time);
    const aValue = Number.isNaN(aTime) ? 0 : aTime;
    const bValue = Number.isNaN(bTime) ? 0 : bTime;

    return bValue - aValue;
  });
};

/**
 * Applies list filters, optional search, and newest-first ordering for display.
 * @param {Array} templates - Full template list from the API.
 * @param {Object} filters - Applied post type, topic, subtopic, availability, and type filters.
 * @param {string} searchQuery - Optional message or id search term.
 * @param {boolean} showInactive - When false, expired templates stay hidden
 * unless an explicit availability filter is applied. Defaults to showing them.
 * @returns {Array} Templates ready to render in the list.
 */
export const getDisplayedTemplates = (
  templates,
  filters,
  searchQuery = '',
  showInactive = true
) => {
  const filtered = filterTemplates(templates, filters, showInactive);
  const searched = searchQuery
    ? searchTemplates(filtered, searchQuery)
    : filtered;

  const ordered = filters.sortBy
    ? sortTemplatesByChoice(searched, filters.sortBy)
    : sortTemplatesByCreatedTime(searched);

  return ordered.sort((a, b) => {
    const aActive = isTemplateActive(a) ? 0 : 1;
    const bActive = isTemplateActive(b) ? 0 : 1;
    return aActive - bActive;
  });
};

const SORT_FIELDS = {
  recently_added: ['created_time', 'created_at'],
  recently_edited: ['audit_time', 'updated_at', 'updatedAt', 'created_time'],
  start_date: ['start_date'],
  end_date: ['end_date'],
};

function firstParsedTime(template, fields) {
  const matched = fields
    .map((field) => Date.parse(template[field]))
    .find((value) => !Number.isNaN(value));

  return matched ?? 0;
}

function sortTemplatesByChoice(templates, sortBy) {
  const fields = SORT_FIELDS[sortBy] || SORT_FIELDS.recently_added;

  return [...templates].sort(
    (a, b) => firstParsedTime(b, fields) - firstParsedTime(a, fields)
  );
}

/**
 * Normalizes a string for case-insensitive, accent-insensitive search.
 * @param {string} str - The string to normalize.
 * @returns {string} The normalized string.
 */
const normalizeString = (str = '') => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Gets the count of active templates for a topic or subtopic with an end_date in the future or no end_date (evergreen).
 * @param {Array} templates - The full list of templates.
 * @param {string} id - The topic or subtopic ID.
 * @param {string} type - 'topic' or 'subtopic'.
 * @returns {number} The number of active templates.
 */
export const getActiveTemplateCount = (templates, id, type) => {
  return templates.filter(
    (template) =>
      templateMatchesTopicOrSubtopic(template, id, type) &&
      isTemplateActive(template)
  ).length;
};

/**
 * Ends all active templates for a given topic or subtopic by setting end_date to now.
 * @param {Array} templates - The full list of templates.
 * @param {string} id - The topic or subtopic ID.
 * @param {string} type - 'topic' or 'subtopic'.
 * @returns {Promise<void>} - Resolves when templates are ended.
 */
export const endActiveTemplatesForTopicOrSubtopic = async (
  templates,
  id,
  type
) => {
  const activeTemplateIds = templates
    .filter(
      (template) =>
        templateMatchesTopicOrSubtopic(template, id, type) &&
        isTemplateActive(template)
    )
    .map((template) => template.id);

  if (activeTemplateIds.length === 0) {
    throw new Error('No active templates to end.');
  }

  await TemplatesApi.endTemplates(activeTemplateIds);
};
